<template>
  <main
  v-if="entry"
  id="content"
  aria-labelledby="main-title"
  >
    <article>

      <div class="hero--info bg-faun">
        <div class="pt-16 sm:pt-24 pb-8">
          <Container>
            <FadeIn class="max-w-3xl mx-auto">
              <nav aria-label="Breadcrumb" class="text-sm mb-4 opacity-70">
                <NuxtLink :to="$localePath('/')">Home</NuxtLink>
                <span class="mx-2">/</span>
                <NuxtLink :to="$localePath('/glossary')">Glossary</NuxtLink>
              </nav>
              <h1 id="main-title" class="text-section-title">
                {{ entry.title }}
              </h1>
              <p v-if="entry.definition_short" class="text-subtitle mt-4">
                {{ entry.definition_short }}
              </p>
            </FadeIn>
          </Container>
        </div>
      </div>

      <Container class="px-4 sm:px-8">
        <div class="max-w-3xl mx-auto prose">

          <ElementByline
          :author="entry?.author_id"
          :reviewer="entry?.reviewer_id"
          :published-date="entry?.date_updated"
          :updated-date="entry?.last_reviewed"
          />

          <section
          v-if="entry.definition_long"
          class="glossary-section"
          >
            <h2>What is {{ primaryTerm }}?</h2>
            <div
            v-interpolation
            v-html="useSanitize(entry.definition_long, currentLocale)"
            ></div>
          </section>

          <section
          v-if="entry.context_body"
          class="glossary-section"
          >
            <h2>{{ capitalise(primaryTerm) }} in Australian workplaces</h2>
            <div
            v-interpolation
            v-html="useSanitize(entry.context_body, currentLocale)"
            ></div>
          </section>

          <section
          v-if="entry.faqs && entry.faqs.length"
          class="glossary-section not-prose"
          >
            <h2 class="prose">Common questions about {{ primaryTerm }}</h2>
            <ElementInlineFaq
            :content="{
              title: '',
              faqs: orderedFaqs
            }"
            />
          </section>

          <section
          v-if="orderedRelatedPillars.length"
          class="glossary-section"
          >
            <h2>Related reading</h2>
            <ul class="glossary-related-list not-prose">
              <li
              v-for="(pillar, index) in orderedRelatedPillars"
              :key="'pillar-'+index"
              >
                <NuxtLink
                :to="$localePath(articleHref(pillar.article_id.slug))"
                class="glossary-related-link"
                >
                  <span class="font-semibold">{{ pillar.article_id.title }}</span>
                </NuxtLink>
              </li>
            </ul>
          </section>

          <section
          v-if="orderedRelatedTerms.length"
          class="glossary-section"
          >
            <h2>See also</h2>
            <ul class="glossary-related-list not-prose">
              <li
              v-for="(term, index) in orderedRelatedTerms"
              :key="'term-'+index"
              >
                <NuxtLink
                :to="$localePath(`/glossary/${term.related_glossary_term_id.slug}`)"
                class="glossary-related-link"
                >
                  <span class="font-semibold">{{ term.related_glossary_term_id.title }}</span>
                  <span
                  v-if="term.related_glossary_term_id.definition_short"
                  class="block text-sm opacity-70 mt-1"
                  >
                    {{ term.related_glossary_term_id.definition_short }}
                  </span>
                </NuxtLink>
              </li>
            </ul>
          </section>

          <section
          v-if="entry.sources"
          class="glossary-section"
          >
            <h2>Sources</h2>
            <div
            class="glossary-sources"
            v-interpolation
            v-html="useSanitize(entry.sources, currentLocale)"
            ></div>
          </section>

          <footer class="glossary-footer mt-12 pt-6 border-t border-[#d7d4cd] text-sm opacity-70">
            <span v-if="entry.last_reviewed">Last reviewed: {{ formatDate(entry.last_reviewed) }}</span>
          </footer>

          <ElementLegalDisclaimer v-if="!entry?.suppress_legal_disclaimer" />

        </div>
      </Container>

    </article>

    <ElementBookDemoCta />

  </main>
</template>

<script setup>
// Remount on every navigation (its own page key). This page emits JSON-LD via
// useSchemaOrg, whose graph is not cleanly replaced on client-side nav between schema
// pages; remounting re-renders it from scratch. See specs/remove-global-page-key.md.
definePageMeta({ key: route => route.fullPath })

// Publish gate: an unbuilt CMS page 404s until the next deploy prerenders it.
useRequirePrerendered()

const { getItems } = useCms()
const route = useRoute()

const currentLocale = computed(() => {
  return route.path.startsWith('/nz') ? 'nz' : 'au'
})

const glossaryFields = [
  '*',
  'seo.meta_title',
  'seo.meta_description',
  'seo.meta_image.id',
  'seo.meta_image.filename_download',
  'faqs.id',
  'faqs.sort',
  'faqs.faq_item_id.id',
  'faqs.faq_item_id.question',
  'faqs.faq_item_id.answer',
  'related_pillars.id',
  'related_pillars.sort',
  'related_pillars.article_id.id',
  'related_pillars.article_id.slug',
  'related_pillars.article_id.title',
  'related_terms.id',
  'related_terms.sort',
  'related_terms.related_glossary_term_id.id',
  'related_terms.related_glossary_term_id.slug',
  'related_terms.related_glossary_term_id.title',
  'related_terms.related_glossary_term_id.definition_short',
  'author_id.id',
  'author_id.title',
  'author_id.slug',
  'author_id.position',
  'author_id.credentials',
  'author_id.schema_type',
  'author_id.linkedinUrl',
  'reviewer_id.id',
  'reviewer_id.title',
  'reviewer_id.slug',
  'reviewer_id.position',
  'reviewer_id.credentials',
  'reviewer_id.schema_type',
  'reviewer_id.linkedinUrl'
]

const { data } = await useAsyncData(
  currentLocale.value+'-glossary-'+route.params.slug,
  () => getItems({
    collection: 'glossary_term',
    params: {
      filter: {
        locale: { _contains: currentLocale.value },
        slug: { _eq: route.params.slug },
        status: { _in: useRuntimeConfig().public.postStatus }
      },
      fields: glossaryFields
    }
  })
)

if (!data.value?.length) {
  throw createError({
    statusCode: 404,
    message: 'Glossary term not found',
    fatal: true
  })
}

const entry = data.value[0]

const primaryTerm = computed(() => {
  if (entry?.primary_keyword) return entry.primary_keyword
  // Fallback: first word of the title before the first colon
  return (entry?.title || '').split(':')[0].trim()
})

const orderedFaqs = computed(() => {
  return [...(entry?.faqs || [])]
    .filter(f => f?.faq_item_id)
    .sort((a, b) => (a.sort || 0) - (b.sort || 0))
})

const orderedRelatedPillars = computed(() => {
  return [...(entry?.related_pillars || [])]
    .filter(r => r?.article_id)
    .sort((a, b) => (a.sort || 0) - (b.sort || 0))
})

const orderedRelatedTerms = computed(() => {
  return [...(entry?.related_terms || [])]
    .filter(r => r?.related_glossary_term_id)
    .sort((a, b) => (a.sort || 0) - (b.sort || 0))
})

const capitalise = (str) => {
  if (!str) return ''
  return str.charAt(0).toUpperCase() + str.slice(1)
}

const articleHref = (slug) => `/articles/${slug}`

const formatDate = (isoDate) => {
  if (!isoDate) return ''
  const d = new Date(isoDate)
  return d.toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' })
}

const seo = computed(() => {
  if (entry?.seo) {
    return useSeo(entry.seo, route.fullPath)
  }
  return {}
})

useHead(seo)

const elkerOrganization = {
  '@type': 'Organization',
  name: 'Elker',
  url: 'https://elker.com'
}

const buildAuthorNode = (person, fallback) => {
  if (!person) return fallback
  const node = {
    '@type': person.schema_type === 'organization' ? 'Organization' : 'Person',
    name: person.title,
    url: 'https://elker.com/about'
  }
  if (person.linkedinUrl) {
    node.sameAs = [person.linkedinUrl]
  }
  if (person.schema_type !== 'organization') {
    if (person.position) node.jobTitle = person.position
    node.worksFor = elkerOrganization
    if (person.credentials) {
      node.hasCredential = person.credentials
        .split(',')
        .map(part => part.trim())
        .filter(Boolean)
        .map(name => ({
          '@type': 'EducationalOccupationalCredential',
          credentialCategory: 'degree',
          name
        }))
    }
  }
  return node
}

const localePrefix = currentLocale.value === 'nz' ? '/nz' : ''

const definedTermSchema = {
  '@type': 'DefinedTerm',
  name: entry?.title || '',
  description: entry?.definition_short || '',
  inDefinedTermSet: `https://elker.com${localePrefix}/glossary`,
  url: `https://elker.com${route.fullPath}`
}

const schemaBlocks = [
  definedTermSchema,
  {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `https://elker.com${localePrefix}/` },
      { '@type': 'ListItem', position: 2, name: 'Glossary', item: `https://elker.com${localePrefix}/glossary` },
      { '@type': 'ListItem', position: 3, name: entry?.title || '', item: `https://elker.com${route.fullPath}` }
    ]
  },
  ...(orderedFaqs.value.length
    ? [{
        '@type': 'FAQPage',
        mainEntity: orderedFaqs.value.map(f => ({
          '@type': 'Question',
          name: f.faq_item_id.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: f.faq_item_id.answer
          }
        }))
      }]
    : [])
]

const webPageProps = {}
if (entry?.author_id) {
  webPageProps.author = buildAuthorNode(entry.author_id, elkerOrganization)
}
if (entry?.reviewer_id) {
  webPageProps.reviewedBy = buildAuthorNode(entry.reviewer_id, null)
}
if (Object.keys(webPageProps).length) {
  schemaBlocks.push(defineWebPage(webPageProps))
}

useSchemaOrg(schemaBlocks)
</script>

<style scoped>
.glossary-section {
  margin-top: 2.5rem;
}

.glossary-related-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 0.75rem;
}

.glossary-related-link {
  display: block;
  padding: 1rem;
  border: 2px solid #d7d4cd;
  border-radius: 0.5rem;
  text-decoration: none;
  color: inherit;
  transition: border-color 0.2s;
}

.glossary-related-link:hover {
  border-color: #5a6ded;
}

.glossary-sources :deep(ol),
.glossary-sources :deep(ul) {
  padding-left: 1.5rem;
}

.glossary-footer {
  font-size: 0.875rem;
}
</style>
